import React, { useState } from 'react';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import { BsArrowLeft } from 'react-icons/bs';

const ToggleSwitch = ({ name, checked, onChange }) => (
  <div className="flex items-center gap-2">
    <label htmlFor={name} className="relative inline-block w-12 h-6 cursor-pointer">
      <input
        type="checkbox"
        id={name}
        name={name}
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      {/* Background track */}
      <div
        className={`w-full h-full rounded-full transition-colors ${
          checked ? 'bg-green-500' : 'bg-gray-300'
        }`}
      ></div>
      {/* Slider circle */}
      <div
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-6' : ''
        }`}
      ></div>
    </label>
    <span className="text-sm capitalize">{name.replace(/([A-Z])/g, ' $1')}</span>
  </div>
);



const ManageItems = () => {
  const [variants, setVariants] = useState([]);
  const [showAddVariant, setShowAddVariant] = useState(false);

  const handleAddVariant = (variant) => {
    setVariants([...variants, variant]);
    setShowAddVariant(false);
  };

  const handleDeleteVariant = (index) => {
    const updated = [...variants];
    updated.splice(index, 1);
    setVariants(updated);
  };

  return (
    <div className="min-h-screen bg-white px-4 py-6 sm:px-8">
      <div className="flex items-center mb-4">
       
        <h1 className="text-xl font-semibold">Manage Items</h1>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block font-semibold">Item Name*</label>
          <input type="text" className="w-full border p-2 rounded" placeholder="Enter Item Name" />
        </div>
        <div>
          <label className="block font-semibold">Category*</label>
          <select className="w-full border p-2 rounded">
            <option>Select Category</option>
          </select>
        </div>
        <div>
          <label className="block font-semibold">Sell By Unit or Fraction*</label>
          <select className="w-full border p-2 rounded">
            <option>Select Sell By</option>
          </select>
        </div>

        {variants.map((variant, index) => (
          <div key={index} className="flex flex-col sm:flex-row justify-between items-center p-4 border rounded shadow mb-2">
            <div className="flex items-center mb-2 sm:mb-0">
              <img src={variant.image} alt="variant" className="w-12 h-12 rounded-full mr-3" />
              <span className="font-semibold text-lg">{variant.name}</span>
            </div>
            <div className="flex flex-wrap gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Selling Price</p>
                <p className="font-medium">${variant.sellingPrice}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Purchase Price</p>
                <p className="font-medium">${variant.purchasePrice}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Stock Available</p>
                <p className="font-medium">{variant.stock}</p>
              </div>
              <button onClick={() => handleDeleteVariant(index)} className="text-primary">
                <FaTrash />
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={() => setShowAddVariant(true)}
          className="flex items-center gap-2 text-primary font-semibold mt-4"
        >
          <FaPlus /> Add Variant
        </button>
      </div>

      <button className="w-full bg-primary text-white py-4 rounded-lg text-center font-semibold text-lg mt-8 mb-4 hover:bg-[#b01e38] transition-colors">
        Save
      </button>

      {showAddVariant && <AddVariantModal onClose={() => setShowAddVariant(false)} onAdd={handleAddVariant} />}
    </div>
  );
};

const AddVariantModal = ({ onClose, onAdd }) => {
  const [variant, setVariant] = useState({
    name: '',
    sellingPrice: '',
    purchasePrice: '',
    stock: '',
    lowStockLimit: '',
    trackProfit: false,
    lowStockAlert: false,
    autoUpdateStock: false,
    image: 'https://via.placeholder.com/50',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVariant({
      ...variant,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const calculateMargin = () => {
    const selling = parseFloat(variant.sellingPrice);
    const purchase = parseFloat(variant.purchasePrice);
    if (!isNaN(selling) && !isNaN(purchase) && purchase > 0) {
      return (((selling - purchase) / purchase) * 100).toFixed(2);
    }
    return '0.00';
  };

  const toggleField = (field) => {
    setVariant(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = () => {
    onAdd(variant);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
      <div className="bg-white w-full max-w-md p-6 rounded-lg shadow space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Add Variant</h2>
          <button onClick={onClose} className="text-primary text-lg">×</button>
        </div>

        <div className="text-center">
          <img src={variant.image} alt="Variant" className="w-20 h-20 mx-auto rounded-full" />
          <button className="text-primary mt-2 text-sm flex items-center gap-1"><FaEdit /> Edit Image</button>
        </div>

        <input
          type="text"
          name="name"
          className="w-full border p-2 rounded"
          placeholder="Enter Variant Name"
          onChange={handleChange}
        />

        <div className="flex justify-between items-center">
          <input
            type="number"
            name="sellingPrice"
            className="w-[65%] border p-2 rounded"
            placeholder="Enter Selling Price"
            onChange={handleChange}
          />
          <ToggleSwitch name="trackProfit" checked={variant.trackProfit} onChange={() => toggleField('trackProfit')} />
        </div>

        {variant.trackProfit && (
          <div className="flex justify-between items-center gap-4">
            <input
              type="number"
              name="purchasePrice"
              className="w-[65%] border p-2 rounded"
              placeholder="Enter Purchase Price"
              onChange={handleChange}
            />
            <div className="text-right">
              <label className="block text-sm">Margin (%)</label>
              <input
                type="text"
                readOnly
                className="w-full border p-2 rounded bg-gray-100 text-right"
                value={calculateMargin()}
              />
            </div>
          </div>
        )}

        <input
          type="number"
          name="stock"
          className="w-full border p-2 rounded"
          placeholder="Enter Stock"
          onChange={handleChange}
        />

        <div className="flex items-center justify-between">
          <ToggleSwitch name="lowStockAlert" checked={variant.lowStockAlert} onChange={() => toggleField('lowStockAlert')} />
          <ToggleSwitch name="autoUpdateStock" checked={variant.autoUpdateStock} onChange={() => toggleField('autoUpdateStock')} />
        </div>

        {variant.lowStockAlert && (
          <input
            type="number"
            name="lowStockLimit"
            className="w-full border p-2 rounded"
            placeholder="Enter Low Stock Alert Limit"
            onChange={handleChange}
          />
        )}

        <button onClick={handleSubmit} className="w-full bg-primary text-white py-2 rounded font-semibold">
          Add
        </button>
      </div>
    </div>
  );
};

export default ManageItems;
